import { create } from "zustand";
import type { OwnedVillage } from "@empire/shared";
import { ApiProblem, Unreachable } from "../api/client.js";
import {
  SessionLost,
  clearSession,
  fresh,
  loadSession,
  saveSession,
  signUpAsGuest,
  fetchAccount,
  registerAccount,
  logIn as logInRequest,
  signUp as signUpRequest,
  foundVillage as foundVillageRequest,
  type Session,
} from "../api/session.js";

/**
 * Who the app is signed in as (S5-1), and how sure it is about that.
 *
 * Five states rather than a boolean, because "we have never asked", "we asked
 * and nobody answered" and "we asked and were turned away" need three different
 * things on screen and two different buttons.
 */
export type SessionStatus =
  | "unknown"
  | "connecting"
  /** Signed in, with a village to save into. */
  | "ready"
  /** We have credentials, but the server is not answering. */
  | "offline"
  /** The server refused them. Only a new account gets past this. */
  | "lost"
  /** No account on this device: the welcome screen asks to sign up or log in (D-117). */
  | "signedOut";

interface SessionState {
  status: SessionStatus;
  session: Session | null;
  villageId: string | null;
  /**
   * Why signing in failed, in the server's words. Without this every failure
   * looked the same on screen — "No village yet." — whether the server had no
   * database, refused the tenth sign-up in an hour, or was simply asleep.
   */
  error: string | null;
  /**
   * The saved username (D-116): a string once saved, null for a guest, and
   * undefined until the server has been asked.
   */
  username: string | null | undefined;
  /** The account's villages (D-117); undefined until the server has been asked. */
  villages: OwnedVillage[] | undefined;

  /** Sign in on first open, or resume the stored session. */
  connect: () => Promise<void>;
  /** An access token good for the next minute, or null if there is no session. */
  token: () => Promise<string | null>;
  /** Abandon the current account and start a fresh one. Destructive, and asked for. */
  startOver: () => Promise<void>;
  /** Ask the server whether this account is saved under a username. */
  loadAccount: () => Promise<void>;
  /** Save this village under a username. Resolves to an error message, or null. */
  register: (username: string, password: string) => Promise<string | null>;
  /** Open a saved account on this device. Resolves to an error message, or null. */
  logIn: (username: string, password: string) => Promise<string | null>;
  /** Create an account (D-117). Resolves to an error message, or null. */
  signUp: (username: string, password: string) => Promise<string | null>;
  /** Found this account's village. Resolves to an error message, or null. */
  foundVillage: (name: string) => Promise<string | null>;
  /** Play this village. */
  enterVillage: (villageId: string) => void;
  /** Forget this device's session and go back to the welcome screen. */
  logOut: () => void;
}

function problemText(error: unknown, fallback: string): string {
  if (error instanceof Unreachable) return "Could not reach the server. Try again in a moment.";
  if (error instanceof ApiProblem) return error.message;
  return fallback;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  status: "unknown",
  session: null,
  villageId: null,
  error: null,
  username: undefined,
  villages: undefined,

  connect: async () => {
    if (get().status === "connecting") return;
    set({ status: "connecting", error: null });

    const stored = loadSession();
    try {
      // A stored session is used as-is; only an actually-expired access token
      // costs a round trip. Signing up again because we *could* is how you end
      // up with a map full of abandoned villages.
      // No account on this device: the welcome screen, not a silent guest
      // account (D-117). Guests from before keep playing as they were.
      if (!stored) {
        set({ status: "signedOut", session: null, villageId: null, error: null });
        return;
      }
      const session = await fresh(stored);
      set({ status: "ready", session, villageId: session.villageId || null, error: null });
    } catch (error) {
      if (error instanceof Unreachable) {
        // Keep whatever we had: the credentials are almost certainly fine.
        set({
          status: "offline",
          session: stored,
          villageId: stored?.villageId || null,
          error: "Could not reach the server.",
        });
        return;
      }
      if (error instanceof SessionLost) {
        // With accounts (D-117) a refused session is not a lost village: log
        // in again. A guest from before has nothing to log in with.
        clearSession();
        set({
          status: "signedOut",
          session: null,
          villageId: null,
          error: "You were signed out. Log in again.",
        });
        return;
      }
      // Anything else — the server has no database, too many new accounts from
      // this address in an hour, a bad answer — used to be thrown into the
      // void, leaving the app with no session and no explanation. It is a
      // state the player can do something about, so it says so and can be
      // retried.
      set({
        status: "offline",
        session: stored,
        villageId: stored?.villageId || null,
        error: error instanceof ApiProblem ? error.message : "Could not sign in.",
      });
    }
  },

  token: async () => {
    const current = get().session;
    if (!current) return null;
    try {
      const session = await fresh(current);
      if (session !== current) set({ session, status: "ready" });
      return session.accessToken;
    } catch (error) {
      if (error instanceof SessionLost) {
        clearSession();
        set({
          status: "signedOut",
          session: null,
          villageId: null,
          error: "You were signed out. Log in again.",
        });
        return null;
      }
      set({ status: "offline" });
      return null;
    }
  },

  startOver: async () => {
    clearSession();
    set({ status: "connecting", session: null, villageId: null, error: null });
    try {
      const session = await signUpAsGuest();
      saveSession(session);
      set({ status: "ready", session, villageId: session.villageId, error: null });
    } catch (error) {
      // Thrown into the void, this left the screen on "connecting" with
      // nothing to explain it.
      set({
        status: "offline",
        error:
          error instanceof Unreachable
            ? "Could not reach the server."
            : error instanceof ApiProblem
              ? error.message
              : "Could not start a new village.",
      });
    }
  },

  loadAccount: async () => {
    const token = await get().token();
    if (!token) return;
    try {
      const account = await fetchAccount(token);
      set({ username: account.username, villages: account.villages });
    } catch {
      /* unknown stays unknown; the save prompt simply waits */
    }
  },

  register: async (username, password) => {
    const token = await get().token();
    if (!token) return "Not signed in yet. Try again in a moment.";
    try {
      set({ username: await registerAccount(token, username.trim(), password) });
      return null;
    } catch (error) {
      return problemText(error, "Could not save your village.");
    }
  },

  logIn: async (username, password) => {
    try {
      const session = await logInRequest(username.trim(), password);
      // No village yet: the choose (or found) screen decides, once the
      // account's villages have arrived.
      set({
        status: "ready",
        session,
        villageId: null,
        error: null,
        username: undefined,
        villages: undefined,
      });
      await get().loadAccount();
      return null;
    } catch (error) {
      return problemText(error, "Could not log in.");
    }
  },

  signUp: async (username, password) => {
    try {
      const session = await signUpRequest(username.trim(), password);
      set({
        status: "ready",
        session,
        villageId: null,
        error: null,
        username: username.trim(),
        villages: [],
      });
      return null;
    } catch (error) {
      return problemText(error, "Could not create your account.");
    }
  },

  foundVillage: async (name) => {
    const token = await get().token();
    if (!token) return "Not signed in. Log in again.";
    try {
      const village = await foundVillageRequest(token, name.trim());
      set({ villages: [village] });
      get().enterVillage(village.villageId);
      return null;
    } catch (error) {
      return problemText(error, "Could not found your village.");
    }
  },

  enterVillage: (villageId) => {
    const session = get().session;
    if (!session) return;
    set({ session: saveSession({ ...session, villageId }), villageId });
  },

  logOut: () => {
    clearSession();
    // Every store holds the old village's state; starting the page again is
    // the one way to be sure none of it survives into the next account.
    globalThis.location?.reload();
  },
}));
